import { AwsUtil } from './../authentication/aws.service';
import { EOMBucketing } from './../bucket/eom-bucketing';
import { BucketedBucketing } from './../bucket/bucketed-bucketing';
import { DailyBucketing } from './../bucket/daily-bucketing';
import { AppConfig } from './../app.config';
import { BucketDef } from './../bucket/bucket-def';
import { BucketingStrategy } from './../bucket/bucketing-strategy';
import { DashboardEventService } from './../dashboard/dashboard-event.service';
import { SnapshotService } from './../snapshot/snapshot.service';
import { CognitoUtil } from './../authentication/cognito.service';
import { AppEnvironment } from './../app-env';
import { environment } from '../../environments/environment';
import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BasePollingRestService } from '../service/base-polling-rest.service';
import { PollableService } from '../service/pollable-service';
import { ServiceRegistry } from '../service/service-registry.service';
import { Observable, Subscription, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import * as moment from 'moment';
import { CurrencyUtil } from '@liquitics/transaction';
import { generateEventDatetime } from '@liquitics/transaction';
import {
    SurvivalHorizonData,
    GetSurvivalHorizonServiceDataRequest,
    GetSurvivalHorizonServiceDataOutput,
    WebApiEndpoints,
    AllocatedBuffer,
} from '@liquitics/transaction';

@Injectable()
export class SurvivalHorizonService extends BasePollingRestService<GetSurvivalHorizonServiceDataOutput> implements PollableService {
    private POLLING_INTERVAL = 5000;
    private HORIZON_DAYS = 180;

    private dataSubject$: Subject<GetSurvivalHorizonServiceDataOutput>;

    private last: Date;
    private zeros: SurvivalHorizonData[] = [];
    private bucketingStrategies: BucketingStrategy[] = [];

    private selectedCurrency: string;
    private selectedAccounts: string[];

    private unsavedBufferFlows: SurvivalHorizonData[] = [];

    constructor(protected http: HttpClient,
        protected awsUtil: AwsUtil,
        protected serviceRegistry: ServiceRegistry,
        protected dashboardEventService: DashboardEventService,
        private datePipe: DatePipe,
        private snapshotService: SnapshotService,
        private appConfig: AppConfig) {

        super(http, awsUtil, serviceRegistry, dashboardEventService);
        this.initializeWithToday();

        this.createPollingDataSubject(this.POLLING_INTERVAL);
        this.dataSubject$ = this.getPollingDataSubject() as Subject<GetSurvivalHorizonServiceDataOutput>;
    }

    initializeWithToday() {
        let m;
        if (this.appConfig.asOfDateMode) {
            m = this.appConfig.asOfDatetimeUTC.clone();
        } else {
            m = this.appConfig.getTodayStartUTC().clone();
        }
        const todayZero = new SurvivalHorizonData();
        todayZero.bucket = m.format('YYYY-MM-DD');
        this.zeros = [];
        this.zeros.push(todayZero);
        for (let i = 1; i <= this.HORIZON_DAYS; i++) {
            m.add(1, 'day');
            const zero = new SurvivalHorizonData();
            zero.bucket = m.format('YYYY-MM-DD');
            this.zeros.push(zero);
        }
        this.bucketingStrategies = [];
        this.bucketingStrategies.push(new DailyBucketing(todayZero.bucket, true));
        this.bucketingStrategies.push(new BucketedBucketing(todayZero.bucket, true));
        this.bucketingStrategies.push(new EOMBucketing(todayZero.bucket, true));
        this.selectedCurrency = null;
        this.selectedAccounts = null;
    }

    getDataSubject() {
        return this.dataSubject$;
    }

    getBucketingStrategies(): BucketingStrategy[] {
        return this.bucketingStrategies;
    }

    setSelectedCurrency(currency: string) {
        this.selectedCurrency = currency;
    }

    setSelectedAccounts(accounts: string[]) {
        this.selectedAccounts = accounts;
    }

    // subscribeToSurvivalHorizonData(observer: (observable: Observable<SurvivalHorizonData[]>) => void, error? : ): Subscription {
    //     return this.dataStream$.subscribe({next: observer);
    // }

    getAPIName(params?: any): string {
        return WebApiEndpoints.QuerySurvivalHorizonPositions;
    }

    useDemoDataFile(): boolean {
        return AppEnvironment.devMode;
    }

    buildPollingRequestParams() {
        const request = new GetSurvivalHorizonServiceDataRequest();
        request.startDate = this.appConfig.getTodayString();
        request.endDate = this.appConfig.getTodayStartUTC().clone().add(180, 'days').format('YYYY-MM-DD');
        if (this.selectedCurrency) {
            request.currencies = [this.selectedCurrency];
        } else {
            request.currencies = this.appConfig.activeUserProfile.getCurrencies();
        }
        request.accounts = this.selectedAccounts;
        request.userProfileFamily = this.appConfig.activeUserProfile.getProfileFamily();
        request.userProfileType = this.appConfig.activeUserProfile.getProfileType();
        const requestAny: any = request;
        requestAny['experimentalFlags'] = 'newprojections';
        return request;
    }

    getGetSurvivalHorizonServiceDataOutput(maxDatetime?: moment.Moment): Observable<GetSurvivalHorizonServiceDataOutput> {
        const request = this.buildPollingRequestParams();
        if (maxDatetime) {
            const maxDatetimeStr = generateEventDatetime(maxDatetime);
            request.maxEventDatetime = maxDatetimeStr;
        } else if (this.appConfig.asOfDateMode) {
            const maxDatetimeStr = generateEventDatetime(this.appConfig.asOfDatetimeUTC);
            request.maxEventDatetime = maxDatetimeStr;
        }
        return super.getDataObservable(request);
    }

    postResolveData(data: GetSurvivalHorizonServiceDataOutput, params?: any): GetSurvivalHorizonServiceDataOutput {
        const returnData = new GetSurvivalHorizonServiceDataOutput();
        returnData.positions = [];
        const survivalHorizonData: SurvivalHorizonData[] = data.positions;
        if (!survivalHorizonData) {
            return returnData;
        }
        const mappedData = [];
        const currencies = this.appConfig.activeUserProfile.getCurrencies();
        currencies.map(currency => {
            mappedData[currency] = [];
        });
        survivalHorizonData
            .filter(entry => {
                return currencies.includes(entry.currency);
            })
            .map(entry => {
                mappedData[entry.currency][entry.bucket] = entry;
            });

        if (data.systemMessages) {
            this.dashboardEventService.systemMessageSubject.next(data.systemMessages);
        }
        currencies.map(currency => {
            const mappedOrZero = this.zeros.map(zero => {
                let returnZero: SurvivalHorizonData;
                const mapped = mappedData[currency][zero.bucket];
                if (mapped) {
                    returnZero = Object.assign(new SurvivalHorizonData(), mapped);
                } else {
                    returnZero = zero.clone();
                    returnZero.currency = currency;
                }
                returnZero.source = SurvivalHorizonData.SOURCE_LIVE;
                return returnZero;
            });
            // Opening data
            // if (this.snapshotService.transactionDataLoaded &&
            //     this.snapshotService.projectionDataLoaded &&
            //     this.snapshotService.currencyBalanceDataLoaded) {
            // const openingTxnData = this.snapshotService.transactionSurvivalHorizonData(currency);
            const openingTxnData = [];
            // const openingProjectionData = this.snapshotService.projectionSurvivalHorizonData(currency);
            const openingProjectionData = [];
            // const openingPredictionData = this.snapshotService.predictionSurvivalHorizonData(currency);
            // const openingCurrencyBalanceData = this.snapshotService.currencyBalanceSurvivalHorizonData(currency);
            const openingCurrencyBalanceData = [];
            const clonedUnsavedBufferFlows = this.unsavedBufferFlows
                .map((bufferFlow: SurvivalHorizonData) => {
                    return bufferFlow.clone();
                })
            const savedBufferFlows = data.allocatedBuffers
                .map((allocatedBuffer: AllocatedBuffer) => {
                    const bufferFlow = new SurvivalHorizonData()
                    bufferFlow.currency = allocatedBuffer.currency;
                    bufferFlow.bucket = this.appConfig.getTodayString();
                    bufferFlow.bufferAmount = allocatedBuffer.bufferAmount;
                    return bufferFlow;
                })
            returnData.positions.push(...this.blend(mappedOrZero, openingTxnData, openingProjectionData,
                openingCurrencyBalanceData, clonedUnsavedBufferFlows, savedBufferFlows));
            // } else {
            //     // Don't push partial data out, so if the data is incomplete push out just blanks
            //     return [];
            // }
        });
        returnData.positions = this.computeAndScale(returnData.positions);
        returnData.projections = data.projections;
        returnData.clientForecasts = data.clientForecasts;
        returnData.deskForecasts = data.deskForecasts;
        returnData.accountFlows = data.accountFlows;
        returnData.foreignOutflows = data.foreignOutflows;
        returnData.throughput = data.throughput;
        returnData.accountCorrections = data.accountCorrections;
        returnData.fiveDayLadder = data.fiveDayLadder;
        returnData.actualBalances = data.actualBalances;
        returnData.accountFlowBreakdowns = data.accountFlowBreakdowns;
        returnData.allocatedBuffers = data.allocatedBuffers;
        returnData.systemMessages = data.systemMessages;
        returnData.suspenseBalance = data.suspenseBalance;

        const returnDataAny: any = returnData;
        const dataAny: any = data;
        returnDataAny.projectionsNew = dataAny.projectionsNew;

        return returnData;
    }

    computeAndScale(survivalHorizonData: SurvivalHorizonData[], byDesk?: boolean): SurvivalHorizonData[] {
        const cumulatives = {};
        const bufferCumulatives = {};
        return survivalHorizonData.sort((e1, e2) => {
            if (e1.bucket === e2.bucket) {
                if (byDesk) {
                    return e1.desk.localeCompare(e2.desk);
                }
                return 0;
            }
            return e1.bucket < e2.bucket ? -1 : 1;
        }).map(entry => {
            const scalePower = CurrencyUtil.currencyScalePower(entry.currency);
            entry.bufferAmount = CurrencyUtil.roundAndScale(entry.bufferAmount, 2, scalePower);
            // Temp for demo!
            // entry.bufferAmount = CurrencyUtil.roundAndScale(10000000, 2, scalePower);
            // Temp for demo!
            // let rawIn = entry.inAmount;
            // let rawOut = entry.outAmount;
            const computedInAmount = CurrencyUtil.roundAndScale(entry.inAmount, 2, scalePower);
            const computedOutAmount = CurrencyUtil.roundAndScale(-entry.outAmount, 2, scalePower);
            const computedNetAmount = CurrencyUtil.round(this.computeNet(computedInAmount, computedOutAmount), 2);
            const computedBalanceAmount = CurrencyUtil.roundAndScale(entry.currencyBalanceSnapshotAmount, 2, scalePower);

            entry.inAmount = computedInAmount;
            entry.outAmount = computedOutAmount;
            entry.netAmount = computedNetAmount;
            entry.currencyBalanceSnapshotAmount = computedBalanceAmount;
            // entry.netAmount = CurrencyUtil.roundAndScale(this.computeNet(rawIn, -rawOut), 0, scalePower);
            const cumulativeKey = byDesk ? (entry.currency + entry.desk) : entry.currency;
            let cumulative = cumulatives[cumulativeKey] || 0.0;
            cumulative += computedNetAmount + computedBalanceAmount;
            cumulatives[cumulativeKey] = cumulative;
            let bufferCumulative = bufferCumulatives[cumulativeKey] || 0.0;
            bufferCumulative += entry.bufferAmount;
            bufferCumulatives[cumulativeKey] = bufferCumulative;

            entry.cumulativeAmount = CurrencyUtil.round(cumulative, 0);
            entry.bufferAmount = CurrencyUtil.round(bufferCumulative, 0);
            entry.liquiditySurplus = CurrencyUtil.round(this.computeLiquitidySurplus(entry.cumulativeAmount, entry.bufferAmount), 0);
            entry.rowKey = cumulativeKey + entry.bucket;
            return entry;
        });
    }

    getStartOfDayPositions(currency: string): Promise<GetSurvivalHorizonServiceDataOutput> {
        const postEODHours = this.appConfig.getTenantInfo().postEndOfDayHours;
        const startDatetime = this.appConfig.getStartOfDayDatetimeUTC().clone().add(postEODHours, 'hours');
        const startRequest = this.buildPollingRequestParams();
        startRequest.currencies = [currency];
        startRequest.maxEventDatetime = generateEventDatetime(startDatetime);
        return this.getDataObservable(startRequest).pipe(take(1)).toPromise();

        // const returnData: SurvivalHorizonData[] = [];
        // const clonedZeros = this.zeros.map(z => {
        //     const clone = z.clone();
        //     clone.currency = currency;
        //     return clone;
        // });
        // // Opening data
        // const openingTxnData = this.snapshotService.transactionSurvivalHorizonData(currency);
        // const openingProjectionData = this.snapshotService.projectionSurvivalHorizonData(currency);
        // // const openingPredictionData = this.snapshotService.predictionSurvivalHorizonData(currency);
        // const openingCurrencyBalanceData = this.snapshotService.currencyBalanceSurvivalHorizonData(currency);

        // returnData.push(...this.blend(clonedZeros, openingTxnData, openingProjectionData, openingCurrencyBalanceData));
        // return this.computeAndScale(returnData);
    }

    // Assumes:
    // - All are same Currency
    // - Buckets are in YYYY-MM-DD format
    // - Signage are consistent
    // - Unscaled
    blend(...survivalHorizonDataArrays: SurvivalHorizonData[][]): SurvivalHorizonData[] {
        let blended: SurvivalHorizonData[] = [];
        // Concatenate all the arrays together
        survivalHorizonDataArrays.map(array => {
            blended = blended.concat(...array);
        });
        // Scrub the data in case of stale/errorneous data
        const firstDate = this.zeros[0].bucket;
        const lastDate = this.zeros[this.zeros.length - 1].bucket;
        blended = blended
            .filter((data: SurvivalHorizonData) => {
                // Remove erronious data
                return data.bucket >= firstDate && data.bucket <= lastDate;
            })
            .map(data => {
                // Split data by source for later analysis
                if (data.source === SurvivalHorizonData.SOURCE_LIVE) {
                    data.liveAmount = data.netAmount;
                } else if (data.source === SurvivalHorizonData.SOURCE_TRANSACTION_SNAPSHOT) {
                    data.liveAmount = data.transactionSnapshotAmount;
                } else if (data.source === SurvivalHorizonData.SOURCE_ASSUMPTION_SNAPSHOT) {
                    data.liveAmount = data.assumptionSnapshotAmount;
                } else if (data.source === SurvivalHorizonData.SOURCE_PREDICTION_SNAPSHOT) {
                    data.liveAmount = data.preditionSnapshotAmount;
                } else if (data.source === SurvivalHorizonData.SOURCE_CURRENCY_BALANCE_SNAPSHOT) {
                    data.liveAmount = data.currencyBalanceSnapshotAmount;
                }
                return data;
            })
            ;
        // Pre reduce to one entry per bucket date
        const groupedByDate = blended.reduce((grouped, data) => {
            const key = data.bucket;
            grouped[key] = grouped[key] || [];
            grouped[key].push(data);

            return grouped;
        }, {});
        blended = [];
        Object.keys(groupedByDate)
            .map((date: string) => {
                const dataForDate: SurvivalHorizonData[] = groupedByDate[date];
                const reduced: SurvivalHorizonData = dataForDate
                    .sort((a: SurvivalHorizonData, b: SurvivalHorizonData) => {
                        return (a.bucket < b.bucket) ? -1 : 1;
                    })
                    .reduce((prev, current) => {
                        if (!prev) {
                            return current;
                        }
                        prev.bufferAmount += current.bufferAmount;
                        prev.inAmount += current.inAmount;
                        prev.outAmount += current.outAmount;
                        prev.currencyBalanceSnapshotAmount += current.currencyBalanceSnapshotAmount;
                        return prev;
                    });
                blended.push(reduced);
            });
        // //TEMP
        // blended.forEach(entry => {

        //     entry.inAmount = Math.random() * entry.inAmount;
        //     entry.outAmount = Math.random() * entry.outAmount;
        // })
        // //TEMP
        return blended;
    }

    // getData(currencies: string[]) {
    // return this.http.get('assets/demo/data/survival-horizon-data-multiple.json')

    // return this.http.get()
    //     .toPromise()
    //     // .then(res => <SurvivalHorizonData[]>res.json().data)
    //     .then(res => <SurvivalHorizonData[]>res.json())
    //     .then(data => {

    //     });
    // }

    computeNet(inAmount: number, outAmount: number) {
        return inAmount - outAmount;
    }

    computeLiquitidySurplus(cumulativeAmount: number, bufferAmount: number) {
        return bufferAmount + cumulativeAmount;
    }


    bucketDataByBlock(block: string,
        data: SurvivalHorizonData[],
        bucketingStrategy: BucketingStrategy,
        initialBucketDataByBlock: any,
        blockKeyExtractor: (survivalHorizonData: SurvivalHorizonData) => string,
        entryProcessor: (survivalHorizonData: SurvivalHorizonData) => void) {

        const columnPrefix = block + '_';
        const today = this.appConfig.getTodayStartUTC().clone();
        const startDateStr = this.appConfig.getTodayString();
        const endDateStr = this.appConfig.getTodayStartUTC().clone().add(180, 'days').format('YYYY-MM-DD');
        return data
            .filter(entry => {
                return (blockKeyExtractor(entry).toUpperCase() === block.toUpperCase()
                    && entry.bucket >= startDateStr && entry.bucket <= endDateStr);
            })
            .map(entry => {
                // Convert raw row to row stamped with bucket
                const bucketRow = {};
                const bucketDef = bucketingStrategy.getBucket(entry.bucket);
                if (!bucketDef) {
                    bucketRow['bucket'] = 'ERR';
                    console.log('Creating an error bucket row. Found no bucket definition for entry bucket: ' + entry.bucket);
                } else {
                    bucketRow['bucketDef'] = bucketDef;
                    bucketRow['bucket'] = bucketDef.getBucketNameFromString(entry.bucket);
                    const collapsed = (bucketDef.expandable && !bucketDef.isExpanded());
                    bucketRow['collapsed'] = collapsed;
                    bucketRow['bucketEnd'] = entry.bucket; // Stamp original bucket date for later use
                    bucketRow['bucketStart'] = entry.bucket; // Stamp minimum bucket date for later use for bounded range of selection
                }
                Object.keys(entry).map((key, index) => {
                    bucketRow[columnPrefix + key] = entry[key];
                });
                entryProcessor(entry);
                return bucketRow;
            }).reduce(function (bucketedData, row) {
                // Reduce grouped by bucket
                let aggRow = bucketedData[row['bucket']];
                if (!aggRow) {
                    aggRow = {
                        bucket: row['bucket'],
                        bucketStart: row['bucketStart'],
                        bucketEnd: row['bucketEnd'],
                        block: block
                    };
                    // If this is the first expanded entry of a bucket, create a holder bucket row
                    // and add it before we add the aggregated/expanded row
                    const bucketDef: BucketDef = row['bucketDef'];
                    if (bucketDef && bucketDef.isExpanded() &&
                        aggRow['bucket'] === bucketDef.formatMomentName(bucketDef.bucketStartDate) &&
                        !today.isSame(bucketDef.bucketStartDate, 'day')) {
                        // Add the row if its
                        // (1) Expanded
                        // (2) The first expanded row (cause we don't want to add more than
                        //     one folder row so we arbitrarily trigger off the first)
                        // (3) This is not the every first folder row (which is a special case
                        //     where we don't want a folder row because we won't be confused)
                        const folderRow = {
                            bucket: bucketDef.bucketName,
                            bucketStart: row['bucketStart'],
                            bucketEnd: bucketDef.bucketEndDate.format('YYYY-MM-DD'),
                            collapsed: true,
                            folderRow: true,
                            block: block
                        };
                        bucketedData[bucketDef.bucketName] = folderRow;
                    }
                    // Set the Agg Row
                    bucketedData[row['bucket']] = aggRow;
                }
                aggregateSummableValues(row, columnPrefix, aggRow);
                if (row['bucketEnd'] > aggRow['bucketEnd']) {
                    aggRow['bucketEnd'] = row['bucketEnd'];
                }
                if (row['bucketStart'] < aggRow['bucketStart']) {
                    aggRow['bucketStart'] = row['bucketStart'];
                }
                return bucketedData;
            }, initialBucketDataByBlock);
    }

    bucketedBlockDataToTableData(bucketedBlockData: any): any[] {
        let lastFolderRow;
        const tableRows: any[] = Object.keys(bucketedBlockData).map(key => {
            return bucketedBlockData[key];
        })
            .sort((r1, r2) => {
                const r1Date = r1.folderRow ? r1.bucketStart : r1.bucketEnd;
                const r2Date = r2.folderRow ? r2.bucketStart : r2.bucketEnd;
                if (r1Date === r2Date) {
                    if (r1.folderRow) {
                        return -1;
                    }
                    if (r2.folderRow) {
                        return 1;
                    }
                    return 0;
                }
                return r1Date < r2Date ? -1 : 1;
            })
            .map((row: any) => {
                if (row.folderRow) {
                    lastFolderRow = row;
                } else {
                    if (lastFolderRow && (lastFolderRow['bucketEnd'] >= row['bucketStart'])) {
                        aggregateSummableValues(row, lastFolderRow['block'] + '_', lastFolderRow, true);
                    }
                }
                return row;
            })
            ;
        return tableRows;
    }

    setBufferFlows(flows: SurvivalHorizonData[]) {
        this.unsavedBufferFlows = flows;
    }

}


function aggregateSummableValues(row: any, columnPrefix: string, aggRow: any, numbersOnly?: boolean) {
    Object.keys(row).map((key, index) => {
        // Aggregate prev + current row values
        let testValue = aggRow[key];
        if (!testValue) {
            testValue = row[key];
        }
        if (typeof testValue === 'number') {
            const currValue = (typeof row[key] === 'number') ? row[key] : 0.0;
            if (isNonSummableKey(columnPrefix, key)) {
                // Non summable columns
                if (row['bucketEnd'] >= aggRow['bucketEnd']) {
                    aggRow[key] = currValue;
                }
            } else {
                // Summable column
                let prevValue = aggRow[key];
                if (!prevValue) {
                    prevValue = 0;
                }
                aggRow[key] = prevValue + currValue;
            }
        } else {
            if (key !== 'bucketStart' && key !== 'bucketEnd') {
                if (!numbersOnly || ((typeof row[key] === 'number') && !isNonSummableKey(columnPrefix, key))) {
                    aggRow[key] = row[key];
                }
            }
        }
    });
}

function isNonSummableKey(columnPrefix: string, key: string): boolean {
    return (key === columnPrefix + 'cumulativeAmount' ||
        key === columnPrefix + 'bufferAmount' ||
        key === columnPrefix + 'liquiditySurplus');
}
